/**
 * GlobalConfigBanner — banner top con configuración global del documento
 * (Doc 24 §3 Fase 11 v2).
 *
 * Shape v2 a nivel doc — todo per locale en `i18n.{lang}.*`:
 *   - `name` (input)
 *   - `description` (input)
 *   - `purpose` (textarea, opcional) — Doc 24 §2.2
 *   - `tone_audience` (textarea, opcional)
 *   - `ai_global_instructions: string[]` (ListInput, opcional) — Doc 24
 *     §2.2 (en v1 era `string`; v2 migró a lista de bullets editable con
 *     `ListInput` de Fase 7)
 *
 * Eliminado en v2 (Doc 24 §1 + Fase 1 backend): `ai_document_config`
 * (agent_tools, search_restrictions, vocabulary_refs, hints). El schema
 * Pydantic ahora rechaza esos campos con `extra="forbid"`. La
 * configuración del agente a nivel documento ya vive en
 * `purpose + tone_audience + ai_global_instructions`.
 *
 * El banner es plegable: por default colapsado (solo name + description
 * + locale switcher); al expandir muestra purpose + tone_audience +
 * ai_global_instructions.
 */

import { ChevronDown, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ListInput } from './ListInput'
import type { DesignerAction, DesignerState } from './state'
import { SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n/config'

interface GlobalConfigBannerProps {
  state: DesignerState
  dispatch: React.Dispatch<DesignerAction>
  readOnly: boolean
}

/** Coerciona el campo `ai_global_instructions` a `string[]`. v2 lo guarda
 * como lista; pero datos legacy (v1) o snapshots intermedios pueden tener
 * el shape `string`. */
function coerceList(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) return [value]
  return []
}

export function GlobalConfigBanner({
  state,
  dispatch,
  readOnly,
}: GlobalConfigBannerProps) {
  const { t } = useTranslation('templates')
  const [expanded, setExpanded] = useState(false)
  const block = state.definition.i18n[state.activeLocale]

  const allLocales = SUPPORTED_LOCALES
  const activeLocales = Object.keys(state.definition.i18n) as SupportedLocale[]
  const missingLocales = allLocales.filter((l) => !activeLocales.includes(l))

  const aiInstructions = coerceList(block?.ai_global_instructions)

  return (
    <section className="bg-surface border-b border-line" data-testid="global-config-banner">
      {/* Top row — siempre visible */}
      <header className="px-6 py-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
            {t('designer.global_config.eyebrow')}
          </p>
          <p className="text-[11px] text-ink-subtle italic">
            {t('designer.global_config.hint')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Locale switcher */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-ink-subtle">
              {t('designer.global_config.editing_locale')}
            </span>
            {activeLocales.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => dispatch({ type: 'SET_LOCALE', locale: loc })}
                data-testid={`global-config-locale-${loc}`}
                className={
                  loc === state.activeLocale
                    ? 'px-2 py-0.5 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                    : 'px-2 py-0.5 text-xs font-mono uppercase rounded hover:bg-surface-muted text-ink-muted'
                }
              >
                {loc}
              </button>
            ))}
            {!readOnly && missingLocales.length > 0
              ? missingLocales.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => dispatch({ type: 'ADD_LOCALE', locale: loc })}
                    title={t('designer.global_config.add_locale') as string}
                    data-testid={`global-config-add-locale-${loc}`}
                    className="px-2 py-0.5 text-xs font-mono uppercase rounded border border-dashed border-line text-ink-subtle hover:text-ink hover:border-line-strong inline-flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    {loc}
                  </button>
                ))
              : null}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={
              (expanded
                ? t('designer.global_config.collapse')
                : t('designer.global_config.expand')) as string
            }
            data-testid="global-config-toggle"
            className="p-1 rounded text-ink-muted hover:text-ink hover:bg-surface-muted"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </header>

      {/* Name + description — siempre visibles */}
      <div className="px-6 pb-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t('designer.global_config.name_label')}>
          <input
            type="text"
            value={block?.name ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_GLOBAL_I18N',
                locale: state.activeLocale,
                patch: { name: e.target.value },
              })
            }
            disabled={readOnly}
            data-testid="global-config-name"
            className="w-full bg-canvas border border-line rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </Field>

        <Field label={t('designer.global_config.description_label')}>
          {/* Textarea de 3 líneas con resize vertical y scroll interno
              cuando la descripción es larga. Antes era un `<input>` que
              ocultaba parte del texto y obligaba al usuario a
              arrastrar dentro del campo para leer. */}
          <textarea
            value={block?.description ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_GLOBAL_I18N',
                locale: state.activeLocale,
                patch: { description: e.target.value },
              })
            }
            disabled={readOnly}
            rows={3}
            maxLength={500}
            data-testid="global-config-description"
            className="w-full bg-canvas border border-line rounded-md px-3 py-1.5 text-sm leading-relaxed focus:outline-none focus:border-brand disabled:opacity-60 resize-y"
          />
        </Field>
      </div>

      {/* ─── Expanded body (v2: purpose + tone_audience + ai_global_instructions) */}
      {expanded ? (
        <div
          className="px-6 pb-4 space-y-4 border-t border-line pt-4"
          data-testid="global-config-expanded"
        >
          <FieldBlock
            title={t('designer.global_config.purpose_label')}
            hint={t('designer.global_config.purpose_help')}
          >
            <textarea
              value={block?.purpose ?? ''}
              onChange={(e) => {
                const v = e.target.value
                dispatch({
                  type: 'UPDATE_GLOBAL_I18N',
                  locale: state.activeLocale,
                  patch: { purpose: v || null },
                })
              }}
              disabled={readOnly}
              rows={3}
              maxLength={2000}
              placeholder={
                t(
                  'designer.global_config.purpose_placeholder',
                ) as string
              }
              data-testid="global-config-purpose"
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand disabled:opacity-60 resize-y"
            />
          </FieldBlock>

          <FieldBlock
            title={t('designer.global_config.tone_audience_title')}
            hint={t('designer.global_config.tone_audience_hint')}
          >
            <textarea
              value={block?.tone_audience ?? ''}
              onChange={(e) => {
                const v = e.target.value
                dispatch({
                  type: 'UPDATE_GLOBAL_I18N',
                  locale: state.activeLocale,
                  patch: { tone_audience: v || null },
                })
              }}
              disabled={readOnly}
              rows={4}
              maxLength={2000}
              placeholder={
                t(
                  'designer.global_config.tone_audience_placeholder',
                ) as string
              }
              data-testid="global-config-tone-audience"
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand disabled:opacity-60 resize-y"
            />
          </FieldBlock>

          <FieldBlock
            title={t('designer.global_config.ai_global_instructions_v2_label')}
            hint={t('designer.global_config.ai_global_instructions_v2_helper')}
          >
            <ListInput
              items={aiInstructions}
              onChange={(items) =>
                dispatch({
                  type: 'UPDATE_GLOBAL_I18N',
                  locale: state.activeLocale,
                  patch: { ai_global_instructions: items },
                })
              }
              placeholder={
                t(
                  'designer.global_config.ai_global_instructions_v2_placeholder',
                ) as string
              }
              addLabel={
                t(
                  'designer.global_config.ai_global_instructions_v2_add_rule',
                ) as string
              }
              maxItems={20}
              maxItemLength={600}
              readOnly={readOnly}
              testId="global-config-ai-instructions"
            />
          </FieldBlock>
        </div>
      ) : null}
    </section>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────
function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide font-medium text-ink-subtle">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function FieldBlock({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md border border-line p-3">
      <header className="mb-2 flex items-baseline gap-2">
        <p className="text-xs font-semibold text-ink">{title}</p>
      </header>
      <div>{children}</div>
      {hint ? (
        <p className="mt-1.5 text-[11px] text-ink-subtle leading-snug">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
